<template>
    <div class="chart-wrapper">
        <!-- Botão de zoom -->
        <button
            v-if="hasData"
            class="zoom-btn"
            title="Ampliar gráfico"
            @click="showModal = true"
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
        </button>

        <!-- Gráfico normal (compacto) -->
        <div class="chart-block">
            <Chart
                v-if="hasData"
                type="polarArea"
                :data="chartData"
                :options="chartOptions"
                class="w-full"
            />
            <div v-else class="chart-empty">Sem dados</div>
        </div>

        <!-- Modal ampliado -->
        <Teleport to="body">
            <Transition name="modal-fade">
                <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
                    <div class="modal-panel">
                        <div class="modal-header">
                            <span class="modal-title">Contratos Concluídos / Mês</span>
                            <button class="modal-close" @click="showModal = false" title="Fechar">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div class="modal-body">
                            <div class="modal-chart-area">
                                <Chart
                                    type="polarArea"
                                    :data="chartData"
                                    :options="modalChartOptions"
                                    style="width:100%;height:100%;"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script>
import SafeChart from '@/components/SafeChart.vue';

const RADIAL_COLORS = [
    '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa',
    '#93c5fd', '#bfdbfe', '#0ea5e9', '#38bdf8',
    '#7dd3fc', '#bae6fd', '#0284c7', '#0369a1',
];

const MES_ABREV = {
    'Janeiro': 'Jan', 'Fevereiro': 'Fev', 'Março': 'Mar',
    'Abril': 'Abr', 'Maio': 'Mai', 'Junho': 'Jun',
    'Julho': 'Jul', 'Agosto': 'Ago', 'Setembro': 'Set',
    'Outubro': 'Out', 'Novembro': 'Nov', 'Dezembro': 'Dez',
};

function abreviarMeses(labels) {
    if (!labels) return [];
    return labels.map(l => MES_ABREV[l] ?? (l?.slice(0, 3) ?? l));
}

export default {
    name: 'RadialConcluidosMes',
    components: { Chart: SafeChart },
    props: {
        rawData: { type: Object, default: () => ({}) }
    },
    data() {
        return { showModal: false };
    },
    computed: {
        hasData() {
            return this.rawData?.labels?.length && this.rawData?.datasets?.length;
        },
        chartData() {
            if (!this.hasData) return {};
            const datasets = this.rawData.datasets.map(ds => ({
                ...ds,
                backgroundColor: RADIAL_COLORS.slice(0, (ds.data || []).length),
                borderColor: '#0d1117',
                borderWidth: 2,
            }));
            return {
                ...this.rawData,
                labels: abreviarMeses(this.rawData.labels),
                datasets,
            };
        },
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: { color: '#8b949e', font: { size: 11 }, boxWidth: 12 }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(20,20,20,0.95)',
                        titleColor: '#e6edf3',
                        bodyColor: '#8b949e',
                        cornerRadius: 6,
                        padding: 8,
                    }
                },
                scales: {
                    r: {
                        grid: { color: 'rgba(255,255,255,0.06)' },
                        ticks: { color: '#6b7280', backdropColor: 'transparent', font: { size: 10 } }
                    }
                }
            };
        },
        modalChartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        labels: {
                            color: '#c9d1d9',
                            font: { size: 15 },
                            boxWidth: 18,
                            padding: 18,
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(10,10,10,0.96)',
                        titleColor: '#e6edf3',
                        bodyColor: '#8b949e',
                        cornerRadius: 8,
                        padding: 12,
                        titleFont: { size: 14 },
                        bodyFont: { size: 13 },
                    }
                },
                scales: {
                    r: {
                        grid: { color: 'rgba(255,255,255,0.08)' },
                        ticks: {
                            color: '#8b949e',
                            backdropColor: 'transparent',
                            font: { size: 14 },
                        }
                    }
                }
            };
        }
    }
}
</script>

<style scoped>
.chart-wrapper  { position: relative; }
.chart-block    { height: 240px; }
.chart-empty    { display: flex; align-items: center; justify-content: center; height: 100%; color: #6b7280; }

/* ── Botão zoom ─────────────────────────────────────────── */
.zoom-btn {
    position: absolute;
    top: -32px;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 6px;
    cursor: pointer;
    color: #8b949e;
    transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
}
.zoom-btn:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.24); color: #e6edf3; }
.zoom-btn svg   { width: 14px; height: 14px; }

/* ── Modal ──────────────────────────────────────────────── */
.modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0,0,0,0.78);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.modal-panel {
    background: #161b22;
    border: 1px solid rgba(255,255,255,0.11);
    border-radius: 14px;
    width: 96vw;
    max-width: 1400px;
    height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
}

.modal-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: #f97316;
}

.modal-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 7px;
    cursor: pointer;
    color: #8b949e;
    transition: background 150ms ease, color 150ms ease;
}
.modal-close:hover { background: rgba(255,255,255,0.1); color: #e6edf3; }
.modal-close svg   { width: 14px; height: 14px; }

.modal-body {
    flex: 1;
    min-height: 0;
    padding: 28px 32px;
    display: flex;
}

.modal-chart-area {
    flex: 1;
    min-height: 0;
    position: relative;
}

/* ── Transition ─────────────────────────────────────────── */
.modal-fade-enter-active,
.modal-fade-leave-active   { transition: opacity 220ms ease; }
.modal-fade-enter-from,
.modal-fade-leave-to       { opacity: 0; }
.modal-fade-enter-active .modal-panel { transition: transform 220ms cubic-bezier(0.0,0.0,0.2,1), opacity 220ms ease; }
.modal-fade-enter-from .modal-panel   { transform: scale(0.96) translateY(12px); opacity: 0; }

@media (prefers-reduced-motion: reduce) {
    .modal-fade-enter-active,
    .modal-fade-leave-active { transition: none; }
}
</style>
